import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { triggerDownload } from '../utils/unsplashUtils';
import toast from 'react-hot-toast';

/**
 * Unsplash image search and selection component
 * Searches Unsplash API, shows 5 results at a time, allows pagination
 * 
 * @param {Function} onSelect - Callback when image is selected: onSelect(photo)
 * @param {string} initialQuery - Pre-fill search query
 * @param {Object} selectedPhoto - Currently selected photo (for showing selected state)
 */
export default function UnsplashImagePicker({ onSelect, initialQuery = '', selectedPhoto = null }) {
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [results, setResults] = useState([]);
  const [allResults, setAllResults] = useState([]); // Store all results for pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [totalResults, setTotalResults] = useState(0);

  const RESULTS_PER_PAGE = 5;
  const currentResults = results;
  // Always show "Next 5" button if we have results - it will cycle back to start when at the end
  const hasMoreResults = allResults.length > 0;

  // Auto-search if initialQuery is provided
  useEffect(() => {
    if (initialQuery && !hasSearched) {
      setSearchQuery(initialQuery);
      handleSearch(null, initialQuery);
    }
  }, [initialQuery]);

  async function handleSearch(e, queryOverride = null) {
    if (e) {
      e.preventDefault();
      e.stopPropagation(); // Prevent form submission from bubbling to parent form
    }
    const query = queryOverride || searchQuery;
    if (!query.trim()) {
      toast.error('Please enter a search term');
      return;
    }

    setLoading(true);
    setHasSearched(true);
    setCurrentPage(0);

    try {
      // Fetch initial batch (30 results = 6 pages of 5)
      const response = await fetch(
        `https://api.unsplash.com/search/photos?query=${encodeURIComponent(query)}&per_page=30`,
        {
          headers: {
            'Authorization': `Client-ID ${process.env.REACT_APP_UNSPLASH_ACCESS_KEY}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch images');
      }

      const data = await response.json();
      setAllResults(data.results);
      setResults(data.results.slice(0, RESULTS_PER_PAGE));
      setTotalResults(data.total);
      setLoading(false);

      if (data.results.length === 0) {
        toast('No images found. Try a different search term.');
      }
    } catch (error) {
      console.error('Error searching Unsplash:', error);
      toast.error('Failed to search images. Please try again.');
      setLoading(false);
    }
  }

  // Calculate next start index for button label
  const nextStartIndex = (currentPage + 1) * RESULTS_PER_PAGE;

  function handleRefresh() {
    // If we've reached the end of loaded results, cycle back to start
    if (nextStartIndex >= allResults.length) {
      setCurrentPage(0);
      setResults(allResults.slice(0, RESULTS_PER_PAGE));
      toast('Back to first results');
    } else {
      // Show next 5 results
      setCurrentPage(currentPage + 1);
      setResults(allResults.slice(nextStartIndex, nextStartIndex + RESULTS_PER_PAGE));
    }
  }

  async function handleSelectImage(photo) {
    // Trigger download tracking
    if (photo.links?.download_location) {
      await triggerDownload(photo.links.download_location);
    }

    onSelect(photo);
    toast.success('Image selected!');
  }

  return (
    <div className="space-y-4">
      {/* Search Bar */}
      <div 
        onClick={(e) => e.stopPropagation()}
        className="flex gap-2"
      >
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              e.stopPropagation(); // Prevent parent form submission
              handleSearch(e);
            }
          }}
          placeholder="Search Unsplash (e.g., 'challah bread', 'menorah')"
          className="flex-1 px-4 py-3 border-2 border-border rounded-xl focus:outline-none focus:border-primary"
        />
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleSearch(e);
          }}
          disabled={loading}
          className="bg-gradient-to-r from-primary to-primary-light text-white font-semibold px-6 py-3 rounded-xl hover:shadow-lg disabled:opacity-50 transition-all"
        >
          {loading ? 'Searching...' : 'Search'}
        </button>
      </div>

      {/* Results Grid */}
      {hasSearched && !loading && (
        <div className="space-y-4">
          {currentResults.length > 0 ? (
            <>
              {/* Results Count */}
              <div className="flex items-center justify-between text-sm text-text-secondary">
                <span>
                  Showing {currentPage * RESULTS_PER_PAGE + 1}-{Math.min(currentPage * RESULTS_PER_PAGE + currentResults.length, allResults.length)} of {totalResults} results
                </span>
                {hasMoreResults && (
                  <button
                    type="button"
                    onClick={handleRefresh}
                    className="text-primary font-semibold hover:text-primary-dark flex items-center gap-1"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    {nextStartIndex >= allResults.length ? 'Back to Start' : 'Next 5'}
                  </button>
                )}
              </div>

              {/* Image Grid */}
              <div className="grid grid-cols-5 gap-3">
                <AnimatePresence mode="popLayout">
                  {currentResults.map((photo) => (
                    <motion.div
                      key={photo.id}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      className="relative group"
                    >
                      <button
                        type="button"
                        onClick={() => handleSelectImage(photo)}
                        className={`
                          relative block w-full aspect-square rounded-xl overflow-hidden
                          border-4 transition-all
                          ${selectedPhoto?.id === photo.id
                            ? 'border-primary shadow-lg'
                            : 'border-transparent hover:border-primary/50'
                          }
                        `}
                      >
                        {/* Image */}
                        <img
                          src={photo.urls.small}
                          alt={photo.alt_description || 'Unsplash photo'}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        />

                        {/* Overlay on hover */}
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center">
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                            {selectedPhoto?.id === photo.id ? (
                              <div className="bg-primary text-white rounded-full p-2">
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              </div>
                            ) : (
                              <span className="text-white font-semibold text-sm">Select</span>
                            )}
                          </div>
                        </div>

                        {/* Selection badge */}
                        {selectedPhoto?.id === photo.id && (
                          <div className="absolute top-2 right-2 bg-primary text-white rounded-full p-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        )}
                      </button>

                      {/* Photographer attribution (small) */}
                      <p className="text-xs text-text-secondary text-center mt-1 truncate">
                        by {photo.user.name}
                      </p>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </>
          ) : (
            <div className="text-center py-8 text-text-secondary">
              <p>No results found. Try a different search term.</p>
            </div>
          )}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
        </div>
      )}

      {/* Initial State */}
      {!hasSearched && !loading && (
        <div className="text-center py-8 text-text-secondary">
          <svg className="w-16 h-16 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p>Search Unsplash to find the perfect image</p>
        </div>
      )}
    </div>
  );
}


